import { useEffect, useRef, useState } from "react";
import Loader from "../Loader";
import MessageBanner from "../reusables/MessageBanner";
import type { IngredientRow } from "@macro-tracker/macro-tracker-shared";
import { deleteIngredient } from "../../utilities/api";
import { formatMacro } from "../../utilities/formatMacro";

type IngredientDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  ingredient: IngredientRow | null;
  onDeleted?: (ingredientId: number) => void;
};

export default function IngredientDialog({
  isOpen,
  onClose,
  ingredient,
  onDeleted,
}: IngredientDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [bannerMessage, setBannerMessage] = useState<string | null>(null);

  useEffect(() => {
    const modal = dialogRef.current;
    if (!modal) return;

    if (isOpen) {
      modal.showModal();
    } else {
      modal.close();
    }
  }, [isOpen]);

  async function handleDelete(): Promise<void> {
    if (!ingredient) return;
    setIsDeleting(true);
    setDeleteError(null);
    setBannerMessage(null);
    const result = await deleteIngredient(ingredient.id);
    setIsDeleting(false);
    if (result.ok) {
      onDeleted?.(ingredient.id);
      onClose();
    } else if (result.status === 409) {
      setBannerMessage(result.errorMessage);
    } else {
      setDeleteError(result.errorMessage);
    }
  }

  const displayDescription =
    ingredient != null &&
    ingredient.description != null &&
    ingredient.description.trim() !== ""
      ? ingredient.description
      : null;

  return (
    <dialog className="container-item" onClose={onClose} ref={dialogRef}>
      <div className="container-item-header">Ingredient</div>
      {bannerMessage != null ? (
        <MessageBanner
          message={bannerMessage}
          color="bad"
          onClick={() => setBannerMessage(null)}
        />
      ) : null}
      {ingredient == null ? (
        <div className="container-item-body" />
      ) : isDeleting ? (
        <>
          <br />
          <Loader size={1.5} thickness={3} />
        </>
      ) : (
        <div className="container-item-body">
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: "4px 8px",
            }}
          >
            {(
              [
                ["Name", ingredient.name],
                ["Description", displayDescription ?? "—"],
                ["Calories", formatMacro(ingredient.calories)],
                ["Protein", formatMacro(ingredient.protein)],
                ["Carbohydrates", formatMacro(ingredient.carbohydrates)],
                ["Fats", formatMacro(ingredient.fats)],
              ] as const
            ).map(([label, value]) => (
              <li
                key={label}
                style={{
                  display: "grid",
                  gridTemplateColumns: "10rem 1fr",
                  gap: "0.5rem",
                  alignItems: "baseline",
                  marginBottom: "0.55rem",
                }}
              >
                <span style={{ fontWeight: 600 }}>{label}</span>
                <span>{value}</span>
              </li>
            ))}
          </ul>
          {deleteError != null ? (
            <p style={{ margin: "8px", color: "var(--bg-error)" }}>
              {deleteError}
            </p>
          ) : null}
          <div className="modal-button-container">
            <button type="button" className="button" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="button button-danger"
              onClick={() => void handleDelete()}
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </dialog>
  );
}
